import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import loadMessages from "@insite/client-framework/Store/Data/Messages/Handlers/LoadMessages";
import { getMessagesDataView } from "@insite/client-framework/Store/Data/Messages/MessagesSelector";
import translate from "@insite/client-framework/Translate";
import { CartModel, CustomerOrderChangeModel, MessageModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import TextArea, { TextAreaPresentationProps } from "@insite/mobius/TextArea";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import sortBy from "lodash/sortBy";
import React, { useState, useEffect } from "react";
import { css } from "styled-components";
import sendMessage from "@insite/client-framework/Store/Pages/OrderApprovalDetails/Handlers/SendMessage";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import getLocalizedCurrency from "@insite/client-framework/Common/Utilities/getLocalizedCurrency";

interface OwnProps {
    cart: CartModel;
    extendedStyles?: ActivityLogStyles;
}

type Props = OwnProps;

export interface ActivityLogStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    messageTextArea?: TextAreaPresentationProps;
    sendButton?: ButtonPresentationProps;
    messagesContainer?: GridContainerProps;
    messageGridItem?: GridItemProps;
    userNameText?: TypographyPresentationProps;
    messageDateText?: TypographyPresentationProps;
    messageSubjectText?: TypographyPresentationProps;
    messageSubjectRequestedRevisionText?: TypographyPresentationProps;
    messageBodyText?: TypographyPresentationProps;
    changesAccordion?: AccordionPresentationProps;
    changesAccordionSection?: AccordionSectionPresentationProps;
    changesAccordionSectionTitleText?: TypographyPresentationProps;
    changesWrapper?: InjectableCss;
    changesIcon?: IconPresentationProps;
    changesTitleText?: TypographyPresentationProps;
    changesOldValueText?: TypographyPresentationProps;
    changesNewValueText?: TypographyPresentationProps;
    changesProductGridContainer?: GridContainerProps;
    changesProductTitleGridItem?: GridItemProps;
    changesProductTitleLink?: LinkPresentationProps;
    changesProductNumberGridItem?: GridItemProps;
    changesProductNumberLabelText?: TypographyPresentationProps;
    changesProductNumberText?: TypographyPresentationProps;
    changesProductPriceGridItem?: GridItemProps;
    changesProductPriceLabelText?: TypographyPresentationProps;
    changesOldProductPriceText?: TypographyPresentationProps;
    changesProductPriceText?: TypographyPresentationProps;
    changesProductQtyGridItem?: GridItemProps;
    changesProductQtyLabelText?: TypographyPresentationProps;
    changesProductQtyText?: TypographyPresentationProps;
    changesProductOldQtyText?: TypographyPresentationProps;
    changesProductSubtotalGridItem?: GridItemProps;
    changesProductSubtotalLabelText?: TypographyPresentationProps;
    changesProductSubtotalText?: TypographyPresentationProps;
    changesProductOldSubtotalText?: TypographyPresentationProps;
}

export const activityLogStyles: ActivityLogStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 10px 20px;
            font-size: 14px;
        `,
    },
    titleText: {
        variant: "h3",
    },
    messageTextArea: {
        css: css`
            min-width: 100%;
            max-width: 100%;
            min-height: 50px;
        `,
        cssOverrides: {
            formField: css`
                margin-bottom: 10px;
            `,
        },
    },
    messagesContainer: {
        gap: 20,
        css: css`
            border-top: 1px solid ${getColor("common.border")};
            margin-top: 20px;
            padding-top: 15px;

            ${GridItemStyle} {
                margin: 0 10px;
                padding: 10px 0;
            }
        `,
    },
    messageGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    userNameText: {
        size: 14,
        weight: "bold",
    },
    messageDateText: {
        size: 14,
    },
    messageSubjectText: {
        size: 14,
        css: css`
            background: #ededed;
            padding: 3px 6px;
            border-radius: 4px;
        `,
    },
    messageSubjectRequestedRevisionText: {
        size: 14,
        weight: 600,
        css: css`
            background: #ffe699;
            padding: 3px 6px;
            border-radius: 4px;
        `,
    },
    messageBodyText: {
        size: 14,
        color: "text.accent",
        css: css`
            margin-top: 10px;
        `,
    },
    changesAccordion: {
        css: css`
            width: 100%;
        `,
    },
    changesAccordionSection: {
        headerProps: {
            css: css`
                button,
                button:hover,
                button:focus {
                    border: 1px solid transparent;
                    background-color: transparent;
                    padding: 8px 8px 0 0;
                    justify-content: flex-start;
                    font-size: 14px;
                }
            `,
        },
        toggleIconProps: {
            size: 20,
            color: "secondary.main",
            css: css`
                margin-left: auto;
            `,
        },
        panelProps: {
            css: css`
                padding: 0;
                border: none;
            `,
        },
    },
    changesAccordionSectionTitleText: {
        css: css`
            margin-left: 10px;
        `,
    },
    changesWrapper: {
        css: css`
            display: flex;
            padding-top: 10px;
        `,
    },
    changesIcon: {
        size: 18,
    },
    changesTitleText: {
        size: 14,
        css: css`
            margin-left: 10px;
            line-height: 20px;
        `,
    },
    changesOldValueText: {
        size: 14,
        color: "common.disabled",
        css: css`
            text-decoration: line-through;
            margin-right: 5px;
        `,
    },
    changesNewValueText: {
        size: 14,
        weight: 600,
        css: css`
            line-height: 20px;
        `,
    },
    changesProductGridContainer: {
        css: css`
            ${GridOffset} {
                width: 100%;
                margin: 15px 0;
                padding-bottom: 15px;
                border-bottom: 1px solid ${getColor("common.border")};
            }
            ${GridItemStyle} {
                margin: 0;
                padding: 0;
                border-top: none;
            }

            &:last-child {
                ${GridOffset} {
                    margin-bottom: 10px;
                    padding-bottom: 0;
                    border: none;
                }
            }
        `,
    },
    changesProductTitleGridItem: {
        width: 12,
    },
    changesProductTitleLink: {
        css: css`
            margin-bottom: 2px;
        `,
    },
    changesProductNumberGridItem: {
        width: 12,
    },
    changesProductNumberLabelText: {
        weight: 600,
    },
    changesProductPriceGridItem: {
        width: 5,
        css: css`
            flex-direction: column;
        `,
    },
    changesProductPriceLabelText: {
        transform: "uppercase",
        size: 12,
        weight: 400,
        color: "text.accent",
        css: css`
            margin-top: 10px;
        `,
    },
    changesOldProductPriceText: {
        color: "common.disabled",
        css: css`
            text-decoration: line-through;
        `,
    },
    changesProductQtyGridItem: {
        width: 2,
        css: css`
            flex-direction: column;
        `,
    },
    changesProductQtyLabelText: {
        transform: "uppercase",
        size: 12,
        weight: 400,
        color: "text.accent",
        css: css`
            margin-top: 10px;
        `,
    },
    changesProductOldQtyText: {
        color: "common.disabled",
        css: css`
            text-decoration: line-through;
        `,
    },
    changesProductSubtotalGridItem: {
        width: 5,
        css: css`
            flex-direction: column;
            align-items: flex-end;
        `,
    },
    changesProductSubtotalLabelText: {
        transform: "uppercase",
        size: 12,
        weight: 400,
        color: "text.accent",
        css: css`
            margin-top: 10px;
        `,
    },
    changesProductOldSubtotalText: {
        color: "common.disabled",
        css: css`
            text-decoration: line-through;
        `,
    },
};

const ActivityLog: React.FC<Props> = ({ cart, extendedStyles }) => {
    const styles = mergeToNew(activityLogStyles, extendedStyles);
    const dispatch = useAppDispatch();
    const language = useAppSelector(state => state.context.session.language!);
    const { currencySymbol } = cart;
    const messagesDataView = useAppSelector(state =>
        state.pages.orderApprovalDetails.cartId
            ? getMessagesDataView(state, {
                  customerOrderId: state.pages.orderApprovalDetails.cartId,
                  process: "OrderApproval",
                  expand: "changes",
              })
            : null,
    );
    const [message, setMessage] = useState("");
    const messageChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.currentTarget.value);
    };

    const [sortedMessages, setSortedMessages] = useState<MessageModel[]>([]);
    useEffect(() => {
        if (!cart) {
            return;
        }

        dispatch(
            loadMessages({
                customerOrderId: cart.id,
                process: "OrderApproval",
                expand: "changes",
            }),
        );
    }, []);

    useEffect(() => {
        if (messagesDataView?.isLoading || !messagesDataView?.value) {
            return;
        }
        setSortedMessages(sortBy(messagesDataView.value || [], ["createdDate"]).reverse());
    }, [messagesDataView]);

    const sendClickHandler = () => {
        if (!cart) {
            return;
        }

        dispatch(sendMessage({ cart, message }));
        setMessage("");
    };

    const renderChanges = (changes: CustomerOrderChangeModel[]): React.ReactNode[] => {
        const changeNodes: React.ReactNode[] = [];

        const lineAddedChanges = changes.filter(o => o.type === "LineAdded");
        if (lineAddedChanges.length > 0) {
            const lineAddedNode = (
                <Accordion key="linesAdded" headingLevel={2} {...styles.changesAccordion}>
                    <ManagedAccordionSection
                        {...styles.changesAccordionSection}
                        title={
                            <>
                                <Icon src="Plus" {...styles.changesIcon} />
                                <Typography as="span" {...styles.changesTitleText} data-test-selector="action-title">
                                    {translate("Added")}&nbsp;
                                </Typography>
                                <Typography
                                    as="span"
                                    {...styles.changesNewValueText}
                                    data-test-selector="quantity-changed-number-of-items"
                                >
                                    {lineAddedChanges.length}&nbsp;
                                    {lineAddedChanges.length > 1 ? translate("new items") : translate("new item")}
                                </Typography>
                            </>
                        }
                    >
                        {lineAddedChanges.map(change => (
                            <GridContainer
                                key={change.product!.id + "_" + change.unitOfMeasure}
                                {...styles.changesProductGridContainer}
                            >
                                <GridItem {...styles.changesProductTitleGridItem}>
                                    <Link {...styles.changesProductTitleLink} href={change.product?.canonicalUrl}>
                                        {change.product?.productTitle}
                                    </Link>
                                </GridItem>
                                <GridItem {...styles.changesProductNumberGridItem}>
                                    <Typography {...styles.changesProductNumberLabelText}>
                                        {translate("Part #")}
                                    </Typography>
                                    <Typography
                                        {...styles.changesProductNumberText}
                                        data-test-selector="added-product-number"
                                    >
                                        {change.product!.productNumber}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.changesProductPriceGridItem}>
                                    <Typography {...styles.changesProductPriceLabelText}>
                                        {translate("Price")}
                                    </Typography>
                                    <Typography {...styles.changesProductPriceText}>
                                        {change.newProductPriceDisplay +
                                            (change.unitOfMeasure ? " / " + change.unitOfMeasure : "")}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.changesProductQtyGridItem}>
                                    <Typography {...styles.changesProductQtyLabelText}>{translate("QTY")}</Typography>
                                    <Typography
                                        {...styles.changesProductQtyText}
                                        data-test-selector="added-product-qty"
                                    >
                                        {change.newValueDisplay}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.changesProductSubtotalGridItem}>
                                    <Typography {...styles.changesProductSubtotalLabelText}>
                                        {translate("Subtotal")}
                                    </Typography>
                                    <Typography {...styles.changesProductSubtotalText}>
                                        {getLocalizedCurrency({
                                            amount: change.newProductPrice * Number(change.newValue),
                                            currencySymbol,
                                            language,
                                        })}
                                    </Typography>
                                </GridItem>
                            </GridContainer>
                        ))}
                    </ManagedAccordionSection>
                </Accordion>
            );

            changeNodes.push(lineAddedNode);
        }

        const lineQtyChangedChanges = changes.filter(o => o.type === "LineQtyChanged");
        if (lineQtyChangedChanges.length > 0) {
            const lineQtyChangedNode = (
                <Accordion key="linesChanged" headingLevel={2} {...styles.changesAccordion}>
                    <ManagedAccordionSection
                        {...styles.changesAccordionSection}
                        title={
                            <>
                                <Icon src="RefreshCw" {...styles.changesIcon} />
                                <Typography as="span" {...styles.changesTitleText} data-test-selector="action-title">
                                    {translate("Changed quantity of")}&nbsp;
                                </Typography>
                                <Typography
                                    as="span"
                                    {...styles.changesNewValueText}
                                    data-test-selector="quantity-changed-number-of-items"
                                >
                                    {lineQtyChangedChanges.length}&nbsp;
                                    {lineQtyChangedChanges.length > 1
                                        ? translate("Items").toLowerCase()
                                        : translate("Item").toLowerCase()}
                                </Typography>
                            </>
                        }
                    >
                        {lineQtyChangedChanges.map(change => (
                            <GridContainer
                                key={change.product!.id + "_" + change.unitOfMeasure}
                                {...styles.changesProductGridContainer}
                            >
                                <GridItem {...styles.changesProductTitleGridItem}>
                                    <Link {...styles.changesProductTitleLink} href={change.product?.canonicalUrl}>
                                        {change.product?.productTitle}
                                    </Link>
                                </GridItem>
                                <GridItem {...styles.changesProductNumberGridItem}>
                                    <Typography {...styles.changesProductNumberLabelText}>
                                        {translate("Part #")}
                                    </Typography>
                                    <Typography
                                        {...styles.changesProductNumberText}
                                        data-test-selector="changed-product-number"
                                    >
                                        {change.product!.productNumber}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.changesProductPriceGridItem}>
                                    <Typography {...styles.changesProductPriceLabelText}>
                                        {translate("Price")}
                                    </Typography>
                                    {change.newProductPrice !== change.oldProductPrice && (
                                        <Typography {...styles.changesOldProductPriceText}>
                                            {change.oldProductPriceDisplay +
                                                (change.unitOfMeasure ? " / " + change.unitOfMeasure : "")}
                                        </Typography>
                                    )}
                                    <Typography {...styles.changesProductPriceText}>
                                        {change.newProductPriceDisplay +
                                            (change.unitOfMeasure ? " / " + change.unitOfMeasure : "")}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.changesProductQtyGridItem}>
                                    <Typography {...styles.changesProductQtyLabelText}>{translate("QTY")}</Typography>
                                    <Typography {...styles.changesProductOldQtyText} data-test-selector="old-quantity">
                                        {change.oldValueDisplay}
                                    </Typography>
                                    <Typography {...styles.changesProductQtyText} data-test-selector="new-quantity">
                                        {change.newValueDisplay}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.changesProductSubtotalGridItem}>
                                    <Typography {...styles.changesProductSubtotalLabelText}>
                                        {translate("Subtotal")}
                                    </Typography>
                                    <Typography
                                        {...styles.changesProductOldSubtotalText}
                                        data-test-selector="old-subtotal"
                                    >
                                        {getLocalizedCurrency({
                                            amount: change.oldProductPrice * Number(change.oldValue),
                                            currencySymbol,
                                            language,
                                        })}
                                    </Typography>
                                    <Typography
                                        {...styles.changesProductSubtotalText}
                                        data-test-selector="new-subtotal"
                                    >
                                        {getLocalizedCurrency({
                                            amount: change.newProductPrice * Number(change.newValue),
                                            currencySymbol,
                                            language,
                                        })}
                                    </Typography>
                                </GridItem>
                            </GridContainer>
                        ))}
                    </ManagedAccordionSection>
                </Accordion>
            );

            changeNodes.push(lineQtyChangedNode);
        }

        const lineRemovedChanges = changes.filter(o => o.type === "LineRemoved");
        if (lineRemovedChanges.length > 0) {
            const lineRemovedNode = (
                <Accordion key="linesRemoved" headingLevel={2} {...styles.changesAccordion}>
                    <ManagedAccordionSection
                        {...styles.changesAccordionSection}
                        title={
                            <>
                                <Icon src="Minus" {...styles.changesIcon} />
                                <Typography as="span" {...styles.changesTitleText} data-test-selector="action-title">
                                    {translate("Removed")}&nbsp;
                                </Typography>
                                <Typography
                                    as="span"
                                    {...styles.changesNewValueText}
                                    data-test-selector="quantity-changed-number-of-items"
                                >
                                    {lineRemovedChanges.length}&nbsp;
                                    {lineRemovedChanges.length > 1
                                        ? translate("Items").toLowerCase()
                                        : translate("Item").toLowerCase()}
                                </Typography>
                            </>
                        }
                    >
                        {lineRemovedChanges.map(change => (
                            <GridContainer
                                key={change.product!.id + "_" + change.unitOfMeasure}
                                {...styles.changesProductGridContainer}
                            >
                                <GridItem {...styles.changesProductTitleGridItem}>
                                    <Link
                                        {...styles.changesProductTitleLink}
                                        typographyProps={{ decoration: "line-through" }}
                                        href={change.product?.canonicalUrl}
                                    >
                                        {change.product?.productTitle}
                                    </Link>
                                </GridItem>
                                <GridItem {...styles.changesProductNumberGridItem}>
                                    <Typography {...styles.changesProductNumberLabelText} decoration="line-through">
                                        {translate("Part #")}
                                    </Typography>
                                    <Typography
                                        {...styles.changesProductNumberText}
                                        decoration="line-through"
                                        data-test-selector="quantity-removed-part-number"
                                    >
                                        {change.product!.productNumber}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.changesProductPriceGridItem}>
                                    <Typography {...styles.changesProductPriceLabelText}>
                                        {translate("Price")}
                                    </Typography>
                                    <Typography {...styles.changesProductPriceText} decoration="line-through">
                                        {change.oldProductPriceDisplay +
                                            (change.unitOfMeasure ? " / " + change.unitOfMeasure : "")}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.changesProductQtyGridItem}>
                                    <Typography
                                        {...styles.changesProductQtyLabelText}
                                        data-test-selector="quantity-removed-qty-label"
                                    >
                                        {translate("QTY")}
                                    </Typography>
                                    <Typography
                                        {...styles.changesProductQtyText}
                                        decoration="line-through"
                                        data-test-selector="quantity-removed-qty"
                                    >
                                        {change.oldValueDisplay}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.changesProductSubtotalGridItem}>
                                    <Typography {...styles.changesProductSubtotalLabelText}>
                                        {translate("Subtotal")}
                                    </Typography>
                                    <Typography {...styles.changesProductSubtotalText} decoration="line-through">
                                        {getLocalizedCurrency({
                                            amount: change.oldProductPrice * Number(change.oldValue),
                                            currencySymbol,
                                            language,
                                        })}
                                    </Typography>
                                </GridItem>
                            </GridContainer>
                        ))}
                    </ManagedAccordionSection>
                </Accordion>
            );

            changeNodes.push(lineRemovedNode);
        }

        const fulfilmentChangedChanges = changes.filter(o => o.type === "FulfillmentMethodChanged");
        if (fulfilmentChangedChanges.length > 0) {
            const fulfillmentChangedNode = (
                <StyledWrapper key="fulfillmentChanged" {...styles.changesWrapper}>
                    <Icon src="Truck" {...styles.changesIcon} />
                    <Typography as="span" {...styles.changesTitleText}>
                        {translate("Changed fulfillment method")}&nbsp;=&nbsp;
                    </Typography>
                    <Typography as="span" {...styles.changesOldValueText}>
                        {fulfilmentChangedChanges[0].oldValueDisplay}
                    </Typography>
                    <Typography as="span" {...styles.changesNewValueText}>
                        {fulfilmentChangedChanges[0].newValueDisplay}
                    </Typography>
                </StyledWrapper>
            );

            changeNodes.push(fulfillmentChangedNode);
        }

        const totalChangedChanges = changes.filter(o => o.type === "TotalChanged");
        if (totalChangedChanges.length > 0) {
            const totalChangedNode = (
                <StyledWrapper key="totalChanged" {...styles.changesWrapper}>
                    <Icon src="ShoppingCart" {...styles.changesIcon} />
                    <Typography as="span" {...styles.changesTitleText} data-test-selector="total-changed-title">
                        {translate("New Total")}&nbsp;=&nbsp;
                    </Typography>
                    <Typography as="span" {...styles.changesOldValueText} data-test-selector="total-changed-old-value">
                        {totalChangedChanges[0].oldValueDisplay}
                    </Typography>
                    <Typography as="span" {...styles.changesNewValueText} data-test-selector="total-changed-new-value">
                        {totalChangedChanges[0].newValueDisplay}
                    </Typography>
                </StyledWrapper>
            );

            changeNodes.push(totalChangedNode);
        }

        return changeNodes;
    };

    if (!cart) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <TextArea
                {...styles.messageTextArea}
                data-test-selector="activity-log-message-text-area"
                placeholder={translate("Write a message...")}
                value={message}
                onChange={messageChangeHandler}
            />
            <Button
                {...styles.sendButton}
                disabled={!message.trim()}
                onClick={sendClickHandler}
                data-test-selector="activity-log-send-button"
            >
                {translate("Send Message")}
            </Button>
            <GridContainer {...styles.messagesContainer} data-test-selector="activity-log-messages-container">
                {sortedMessages.map((message, index) => (
                    <GridItem
                        key={message.dateToDisplay.toString()}
                        {...styles.messageGridItem}
                        data-test-selector={`activity-log-message-${index}`}
                    >
                        <Typography {...styles.userNameText} data-test-selector={`activity-log-edited-user-${index}`}>
                            {message.displayName}{" "}
                        </Typography>
                        <Typography
                            {...styles.messageDateText}
                            data-test-selector={`activity-log-edited-date-${index}`}
                        >
                            <LocalizedDateTime
                                dateTime={message.dateToDisplay}
                                options={{
                                    year: "numeric",
                                    month: "numeric",
                                    day: "numeric",
                                    hour: "numeric",
                                    minute: "numeric",
                                }}
                                data-test-selector={`activity-log-edited-date-localized-${index}`}
                            />
                        </Typography>
                        <Typography
                            {...(message.subject === "Requested revision"
                                ? styles.messageSubjectRequestedRevisionText
                                : styles.messageSubjectText)}
                            data-test-selector={`activity-log-action-type-${index}`}
                        >
                            {message.subject}
                        </Typography>
                        {message.body && <Typography {...styles.messageBodyText}>{message.body}</Typography>}
                        {message.changes && message.changes.length > 0 && <span>{renderChanges(message.changes)}</span>}
                    </GridItem>
                ))}
            </GridContainer>
        </StyledWrapper>
    );
};

export default ActivityLog;
